import React, { useEffect } from 'react';
import { useAdjudicationStore } from '../store/adjudicationStore';
import { Clock, CheckCircle, AlertCircle, Play, SkipForward } from 'lucide-react';

export const AdjudicationPanel: React.FC = () => {
  const { 
    currentTurn, 
    currentStage, 
    phase, 
    timeRemaining, 
    timerActive,
    tickTimer,
    beginAdjudication,
    endTurn,
    teamActions
  } = useAdjudicationStore();

  useEffect(() => {
    const interval = setInterval(() => {
      tickTimer();
    }, 1000);
    return () => clearInterval(interval);
  }, [tickTimer]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getPhaseColor = () => {
    switch (phase) {
      case 'planning': return 'text-blue-400';
      case 'adjudication': return 'text-yellow-400';
      case 'results': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  return (
    <div className="bg-slate-900/90 border-l border-slate-700 p-4 w-80 flex flex-col h-full">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-slate-100 mb-2">Turn {currentTurn} / 9</h2>
        <div className="flex justify-between items-center text-sm text-slate-400">
          <span>Stage {currentStage}</span>
          <span className={`font-mono font-bold ${getPhaseColor()} uppercase`}>{phase}</span>
        </div>
      </div>

      <div className="bg-slate-800 rounded-lg p-4 mb-6 text-center border border-slate-700">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Clock className={`w-5 h-5 ${timerActive ? 'animate-pulse text-blue-400' : 'text-slate-500'}`} />
          <span className="text-3xl font-mono font-bold text-slate-100">
            {formatTime(timeRemaining)}
          </span>
        </div>
        <div className="text-xs text-slate-500 uppercase tracking-wider">
          {phase === 'planning' ? 'Planning Phase' : 'Adjudication Phase'}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto mb-4">
        <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-green-400" />
          Team Status
        </h3>
        <div className="space-y-2">
          {['Blue', 'Red'].map(team => (
            <div key={team} className="flex justify-between items-center bg-slate-800/50 p-2 rounded border border-slate-700">
              <span className="text-slate-300">{team} Team</span>
              <span className="text-xs font-mono bg-slate-700 px-2 py-1 rounded text-slate-400">
                {teamActions.get(team as any)?.length || 0} Actions
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-2 mt-auto">
        {phase === 'planning' && (
          <button
            onClick={beginAdjudication}
            className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-500 text-white rounded flex items-center justify-center gap-2 transition-colors"
          >
            <Play className="w-4 h-4" />
            Begin Adjudication
          </button>
        )}
        
        {phase === 'results' && (
          <button
            onClick={endTurn}
            className="w-full py-2 px-4 bg-green-600 hover:bg-green-500 text-white rounded flex items-center justify-center gap-2 transition-colors"
          >
            <SkipForward className="w-4 h-4" />
            Next Turn
          </button>
        )}
      </div>
    </div>
  );
};
